import React, { FC, useState, useEffect, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  EAppTimezones,
  getDefaultDashboardRange,
  getSelectedTimeZone,
} from '@/utils/dateTime';
import moment from 'moment';
import {
  SELECTED_TIMEZONE_LOCALSTORAGE_KEY,
  ORGANISM_PROPS,
} from '@/constant/global/global.constant';
import { tid } from '@/utils/testId';
import { DATE_TIME_PICKER_TEST_IDS as TID } from '@/constant/testIds/dateTimePicker.testIds';
import { useDateFilter } from '@/context/dateFilter.context';
import { useTheme } from '@/context/theme.context';
import { LuCalendarDays, LuX } from 'react-icons/lu';
import { Text } from '@/components/atoms/AppText';
import { DateTimeRange, DateTimeRangePickerProps, Shortcut } from './types';
import { DatePickerPopup } from './DatePickerPopup';
import { SHORTCUTS } from './constants';

export type { DateTimeRange } from './types';
dayjs.extend(utc);
dayjs.extend(timezone);

const DateTimeRangePicker: FC<DateTimeRangePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date and time range',
  displayFormat = 'YYYY-MM-DD HH:mm',
  minDate,
  maxDate = new Date(),
  disabled = false,
  showShortcuts = true,
  containerClassName = '',
  inputClassName = '',
  timezone = EAppTimezones.EST,
  onReset,
  defaultDateRange,
  showInput = true,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(!showInput);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [startHour, setStartHour] = useState('00');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('23');
  const [endMinute, setEndMinute] = useState('59');
  const [hoveredDate, setHoveredDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedShortcut, setSelectedShortcut] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const selectedTimezone = getSelectedTimeZone();
  const now = moment().tz(selectedTimezone);
  const currentTime = now.format('hh:mm A');
  const currentTimezone =
    localStorage.getItem(SELECTED_TIMEZONE_LOCALSTORAGE_KEY) ||
    EAppTimezones.EST;
  const tzAbbreviation = moment.tz(currentTimezone).format('z');
  const { colors } = useTheme();
  const { filterValues, isDashboard, setGroupBy, groupBy: ctxGroupBy } = useDateFilter();

  // Draft GroupBy lives here (same level as startDate/endDate) so it persists
  // when the popup is closed without Apply — matching date-draft behaviour.
  const [localGroupBy, setLocalGroupBy] = useState<import('@/context/dateFilter.context').GroupByValue>(ctxGroupBy);

  useEffect(() => {
    if (value.startDate) {
      const start = dayjs(value.startDate).tz(timezone);
      setStartDate(start);
      setStartHour(start.format('HH'));
      setStartMinute(start.format('mm'));
      setCurrentMonth(start);
    } else {
      setStartDate(null);
      setStartHour('00');
      setStartMinute('00');
    }

    if (value.endDate) {
      const end = dayjs(value.endDate).tz(timezone);
      setEndDate(end);
      setEndHour(end.format('HH'));
      setEndMinute(end.format('mm'));
    } else {
      setEndDate(null);
      setEndHour('23');
      setEndMinute('59');
      setSelectedShortcut(null);
    }
  }, [value.startDate, value.endDate, timezone]);

  useEffect(() => {
    if (!showInput) return; // inline mode — parent manages closing
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showInput]);

  useEffect(() => {
    if (isOpen && !value?.startDate && !startDate && !selectedShortcut) {
      resetToDefaultRange();
    }
  }, [isOpen]);

  const handleDateClick = (date: dayjs.Dayjs) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
      setSelectedShortcut(null); // Clear shortcut when manually selecting
    } else {
      if (date.isBefore(startDate)) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
      setSelectedShortcut(null); // Clear shortcut when manually selecting
    }
  };

  const handleShortcut = (shortcut: Shortcut) => {
    let start: dayjs.Dayjs | undefined;
    let end: dayjs.Dayjs | undefined;

    // IMPORTANT: Always use timezone-aware "now"
    const now = dayjs().tz(timezone);

    // Default end = now
    end = now;

    // =========================
    // TODAY
    // =========================
    if (shortcut.label === 'Today') {
      start = now.startOf('day');
      end = now;
    }

    // =========================
    // LAST 2 DAYS (Replaces Yesterday)
    // =========================
    else if (shortcut.label === 'Last 2 Days') {
      start = now.subtract(1, 'day').startOf('day');
      end = now;
    }

    // =========================
    // THIS LOGIC (Calendar based)
    // =========================
    else if (shortcut.label.includes('This')) {
      // THIS WEEK (Sunday → Today)
      if (shortcut.label.includes('Week')) {
        start = now.startOf('week');
      } else if (shortcut.label.includes('Month')) {
        start = now.startOf('month');
      }

      // THIS YEAR (Jan 1 → Today)
      else if (shortcut.label.includes('Year')) {
        start = now.startOf('year');
      }
    }

    // =========================
    // LAST LOGIC (Rolling windows)
    // =========================
    else if (shortcut.label.includes('Last')) {
      // LAST WEEK → last 7 days
      if (shortcut.label.includes('Week')) {
        start = now.subtract(6, 'day').startOf('day');
      }

      // LAST MONTH → last 30 days
      else if (shortcut.label.includes('Month')) {
        start = now.subtract(29, 'day').startOf('day');
      }

      // LAST YEAR → last 365 days
      else if (shortcut.label.includes('Year')) {
        start = now.subtract(364, 'day').startOf('day');
      }
    }

    // =========================
    // APPLY STATE
    // =========================
    if (start && end) {
      setStartDate(start);
      setEndDate(end);
      setCurrentMonth(start);
      setSelectedShortcut(shortcut.label);

      // OPTIONAL: Reset time intelligently
      setStartHour(start.format('HH'));
      setStartMinute(start.format('mm'));
      setEndHour(end.format('HH'));
      setEndMinute(end.format('mm'));
    }
  };
  const getDisplayValue = () => {
    if (filterValues.startDate && filterValues.endDate) {
      const start = dayjs(filterValues.startDate)
        .tz(timezone)
        .format(displayFormat);
      const end = dayjs(filterValues.endDate)
        .tz(timezone)
        .format(displayFormat);

      return `${start} ~ ${end}`;
    }
    if (
      defaultDateRange &&
      defaultDateRange.startDate &&
      defaultDateRange.endDate
    ) {
      const start = dayjs(defaultDateRange.startDate)
        .tz(timezone)
        .format(displayFormat);
      const end = dayjs(defaultDateRange.endDate)
        .tz(timezone)
        .format(displayFormat);
      return `${start} ~ ${end}`;
    }
    return '';
  };
  const handleApply = () => {
    if (startDate && endDate) {
      // Ensure these objects are pinned to the timezone before formatting
      const start = startDate
        .tz(timezone)
        .hour(parseInt(startHour))
        .minute(parseInt(startMinute))
        .second(0);
      const end = endDate
        .tz(timezone)
        .hour(parseInt(endHour))
        .minute(parseInt(endMinute))
        .second(59);

      // Apply the GroupBy draft to context only on Apply (same as date range)
      if (isDashboard) setGroupBy(localGroupBy);

      onChange({
        startDate: start.format('YYYY-MM-DD HH:mm:ss'),
        endDate: end.format('YYYY-MM-DD HH:mm:ss'),
        selectedShortcut,
      });
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
  };

  const handleRemoveFilters = () => {
    onChange({
      startDate: null,
      endDate: null,
      selectedShortcut: null,
    });

    setStartDate(null);
    setEndDate(null);
    setStartHour('00');
    setStartMinute('00');
    setEndHour('23');
    setEndMinute('59');
    setSelectedShortcut(null);

    // Reset both context and local draft groupBy to default when clearing the date range
    if (isDashboard) {
      setGroupBy('day');
      setLocalGroupBy('day');
    }

    onReset?.();
  };

  const handleClear = () => {
    resetToDefaultRange(); // Reset UI only
    // Reset local draft groupBy to day when resetting to default range
    if (isDashboard) setLocalGroupBy('day');
  };

  const resetToDefaultRange = () => {
    const defaultRange = getDefaultDashboardRange(timezone);

    const start = dayjs(defaultRange.startDate).tz(timezone);
    const end = dayjs(defaultRange.endDate).tz(timezone);

    setStartDate(start);
    setEndDate(end);
    setCurrentMonth(start);
    setSelectedShortcut('Last Week');

    setStartHour(start.format('HH'));
    setStartMinute(start.format('mm'));
    setEndHour(end.format('HH'));
    setEndMinute(end.format('mm'));
  };
  const dateRangeDisplayValue = getDisplayValue();

  const pickerPopup = (
    <DatePickerPopup
      containerRef={containerRef}
      showShortcuts={showShortcuts}
      currentMonth={currentMonth}
      startDate={startDate}
      endDate={endDate}
      hoveredDate={hoveredDate}
      startHour={startHour}
      startMinute={startMinute}
      endHour={endHour}
      endMinute={endMinute}
      minDate={minDate}
      maxDate={maxDate}
      selectedShortcut={selectedShortcut}
      onShortcutSelect={handleShortcut}
      onMonthChange={setCurrentMonth}
      onDateClick={handleDateClick}
      onDateHover={setHoveredDate}
      onStartHourChange={setStartHour}
      onStartMinuteChange={setStartMinute}
      onEndHourChange={setEndHour}
      onEndMinuteChange={setEndMinute}
      onClear={handleClear}
      onApply={handleApply}
      onCancel={handleCancel}
      localGroupBy={localGroupBy}
      onGroupByChange={setLocalGroupBy}
      timezoneInfo={{
        currentTimezone,
        timezoneAbbreviation: tzAbbreviation,
        currentTime,
      }}
    />
  );

  // Inline mode: just render the popup directly (used inside header dropdown)
  if (!showInput) {
    return (
      <div
        ref={containerRef}
        data-testid={tid(TID.PICKER_WRAPPER)}
        className={containerClassName}
      >
        {pickerPopup}
      </div>
    );
  }

  const hasRange = !!(filterValues.startDate && filterValues.endDate);
  const fmtTrigger = (d: string) =>
    dayjs(d).tz(timezone).format('MMM D, HH:mm');
  const fmtTriggerCompact = (d: string) =>
    dayjs(d).tz(timezone).format('MMM D');

  return (
    <div
      ref={containerRef}
      data-testid={tid(TID.PICKER_WRAPPER)}
      className={`relative inline-block max-w-full ${containerClassName}`}
    >
      {/* ── Trigger button ── */}
      <button
        onClick={() => !disabled && setIsOpen((v) => !v)}
        data-testid={tid(TID.TOGGLE_BTN)}
        disabled={disabled}
        className={[
          'group flex items-center gap-2 h-9 pl-3 pr-2.5 rounded-xl border transition-all duration-200',
          'text-[11px] font-medium tracking-wide select-none outline-none',
          hasRange
            ? 'border-transparent text-white shadow-lg'
            : [
              'border-gray-200 dark:border-white/[0.08]',
              'bg-white dark:bg-white/[0.04]',
              'text-gray-500 dark:text-gray-400',
              'hover:border-gray-300 dark:hover:border-white/[0.15]',
              'hover:bg-gray-50 dark:hover:bg-white/[0.07]',
              'hover:text-gray-700 dark:hover:text-gray-200',
              'shadow-sm hover:shadow',
            ].join(' '),
          disabled
            ? 'opacity-40 cursor-not-allowed pointer-events-none'
            : 'cursor-pointer',
        ].join(' ')}
        style={
          hasRange
            ? {
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 55%, ${colors.tertiary} 100%)`,
              boxShadow: `0 3px 12px ${colors.primary}55`,
            }
            : {}
        }
      >
        {/* Calendar icon — gradient when no range */}
        <span
          className="flex-shrink-0 flex items-center justify-center"
          style={!hasRange ? { color: colors.primary } : {}}
        >
          <LuCalendarDays className="w-3.5 h-3.5" />
        </span>

        {hasRange ? (
          <>
            <span className="hidden sm:flex items-center gap-1.5 whitespace-nowrap">
              <Text as="span" size="sm" weight="semibold" color="inherit">
                {fmtTrigger(filterValues.startDate as string)}
              </Text>
              <Text as="span" size="xs" color="inherit" className="opacity-50">
                —
              </Text>
              <Text as="span" size="sm" weight="semibold" color="inherit">
                {fmtTrigger(filterValues.endDate as string)}
              </Text>
            </span>
            <span className="flex sm:hidden items-center gap-1 whitespace-nowrap">
              <Text as="span" size="sm" weight="semibold" color="inherit">
                {fmtTriggerCompact(filterValues.startDate as string)}
              </Text>
              <Text as="span" size="xs" color="inherit" className="opacity-50">
                —
              </Text>
              <Text as="span" size="sm" weight="semibold" color="inherit">
                {fmtTriggerCompact(filterValues.endDate as string)}
              </Text>
            </span>
          </>
        ) : (
          <Text
            as="span"
            size="sm"
            color="inherit"
            className="whitespace-nowrap"
          >
            {placeholder}
          </Text>
        )}

        {/* Separator + clear button when range active */}
        {hasRange && (
          <>
            <span className="ml-1 w-px h-3.5 bg-white/30 flex-shrink-0" />
            <span
              data-testid={tid(TID.CLEAR_BTN)}
              className="flex items-center justify-center w-5 h-5 rounded-lg bg-white/10 hover:bg-white/25 transition-colors duration-150 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFilters();
              }}
              title={ORGANISM_PROPS.DATE_PICKER_POPUP.CLEAR_RANGE_TITLE}
            >
              <LuX className="w-2.5 h-2.5" />
            </span>
          </>
        )}

        {/* Chevron indicator when no range */}
        {!hasRange && (
          <svg
            className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 text-gray-400 dark:text-gray-500 ${isOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 4l4 4 4-4" />
          </svg>
        )}
      </button>

      {isOpen && pickerPopup}
    </div>
  );
};

export default DateTimeRangePicker;
